// ─────────────────────────────────────────────────────────────────────────────
// RexyGraphLab — the development-only surface the graph is built in.
//
// It is NOT the production GRAPH tab. The empty GRAPH tab on the homepage stays
// exactly as it is — empty, transparent, returning null — until G3.
//
// This module owns the STORE and therefore owns every mutation. The patch API it
// exposes is the same one a live source would call, which is what makes the
// dynamic-addition proofs meaningful: adding a domain, adding a fourth Personal
// agent and recolouring a borough all go through applyPatch and none of them
// touch the renderer, a shader, or the page.
//
// Scale is switchable at ?stress=SMALL|MEDIUM|LARGE. The counts are never
// silently reduced — ask for LARGE and you get LARGE, with whatever numbers it
// produces.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import RexyGraphViewport from '../RexyGraphViewport';
import RexySpatialGraphThree, {
  FocusRequest, GraphRenderMode, GraphStats,
} from '../RexySpatialGraphThree';
import RexyGraphFilters, {
  GraphFilterState, LIVE_KIND_GROUPS, defaultFilterState,
} from '../RexyGraphFilters';
import { GraphInspectorPanel, GraphSearchPanel } from '../RexyGraphOverlay';
import { RexyGraphStructureTree } from '../RexyGraphStructureTree';
import GraphDock, { dockPanelTitleStyle } from '../graphDock';
import { GraphDockTab, GraphUiState, loadUiState, saveUiState } from '../graphUiState';
import { RexyGraphStore } from '../graphStore';
import { GraphBuffers, GraphVisibility, buildGraphBuffers } from '../graphBuffers';
import { GraphPositions } from '../graphLayout';
import { disposeLayoutWorker, runLayout } from '../graphLayoutRunner';
import { SemanticLevel, levelAlphaFor } from '../graphLod';
import { hashUnit } from '../graphMath';
import { GRAPH_BACKGROUND_HEX, domainSlug } from '../graphPalette';
import { RexyGraphNodeRecord, RexyGraphPatch } from '../graphTypes';
import { LAB_DOMAIN_SLUGS, LAB_CITY_HALL_ID, StaticRexyGraphSource } from './graphLabSeed';
import { STRESS_PRESETS } from './graphStressSeed';
import { SparkBuild, buildSparks } from '../graphSparks';
import { GraphSourceMode, currentGraphSourceMode, sourceModeLabel } from '../graphSourceMode';
// G2.4 — the LIVE path now runs on the canonical topology. The old
// RexyOsStructureGraphSource is no longer imported here and no longer
// calculates domains, agents, memory, workflows, surfaces or services for
// anything the owner sees.
import { OS_ROOT_ID } from '../canonicalTopologyAdapter';
import {
  CanonicalCounts, CanonicalGraphSource, DEFAULT_POLL_MS,
} from '../CanonicalGraphSource';
import {
  OsStructureAuthError, fetchOsRevision, fetchOsStructure,
} from '../osStructureClient';

import type { CanonicalParityIssue as ParityIssue } from '../CanonicalGraphSource';
import { GraphNavIntent, parseRoute } from '../graphRoutes';
import { isLabId } from '../graphSource';

const IS_DEV = process.env.NODE_ENV !== 'production';

/** Which kind-group filter a node kind belongs to. Data, not a switch in a leaf. */
const KIND_GROUP: { [kind: string]: string } = {
  'city-hall': 'domains', domain: 'domains', agent: 'agents',
  'memory-cluster': 'memory', 'memory-record': 'memory', 'memory-file': 'memory',
  'memory-system': 'memory',
  file: 'files', document: 'files', thread: 'files', artifact: 'files', citation: 'files',
  tool: 'systems', database: 'systems', workflow: 'systems', model: 'systems', worker: 'systems',
  service: 'services', surface: 'surfaces', evidence: 'surfaces', 'city-hall-room': 'surfaces',
  unknown: 'files',
};

/**
 * LIVE filter grouping, finer than the synthetic one.
 *
 * Synthetic lumps tool/database/workflow/model into 'systems' because the stress
 * scene has thousands of each and a single toggle is what a renderer harness
 * needs. Live has tens, and the owner needs to isolate exactly one kind — and
 * critically, to see DOMAIN memory apart from AGENT memory, which is the whole
 * two-layer distinction. Same node, different question.
 */
function liveGroupForNode(node: RexyGraphNodeRecord): string {
  const sub = String(((node.metadata || {}) as { [k: string]: unknown }).subKind || '');
  if (node.kind === 'domain' || node.kind === 'city-hall') return 'domains';
  if (node.kind === 'agent') return 'agents';
  if (node.kind === 'memory-system') {
    if (sub === 'domain-hive') return 'domain memory';
    if (sub === 'os-master') return 'os memory';
    return 'agent memory';
  }
  if (node.kind === 'database') return 'databases';
  if (node.kind === 'workflow') return 'workflows';
  if (node.kind === 'model') return 'models';
  if (node.kind === 'tool') return 'tools';
  if (node.kind === 'service') return 'services';
  if (node.kind === 'surface') return 'surfaces';
  return 'surfaces';
}

function presetFromQuery() {
  const q = new URLSearchParams(window.location.search).get('stress');
  if (q && STRESS_PRESETS[q.toUpperCase()]) return STRESS_PRESETS[q.toUpperCase()];
  return undefined;
}

export interface RexyGraphLabProps {
  /**
   * Mounted inside another surface (the homepage GRAPH tab) rather than owning
   * the whole document. Embedded, the lab is a flex CHILD and fills the space
   * left for it; standalone at ?surface=graphlab it is the document. It is a
   * positioning switch and nothing else — the graph, its data and its own
   * background are identical in both.
   */
  embedded?: boolean;
  /**
   * How OPEN leaves the graph. The graph is another navigation surface into
   * pages that already exist, so it hands over a parsed INTENT and the host runs
   * the app's own handlers — never window.location.href, never a remount, never
   * a lost session.
   *
   * STANDALONE THERE IS NO HOST. At ?surface=graphlab the lab returns before
   * AppShell and there is no App state to move, so the default records the
   * resolved intent and reports it instead of pretending to navigate. G3 mounts
   * the graph inside the homepage and passes the real handler.
   */
  onNavigate?: (intent: GraphNavIntent) => void;
}

export default function RexyGraphLab({ embedded, onNavigate }: RexyGraphLabProps = {}): React.ReactElement {
  const store = useMemo(() => new RexyGraphStore(), []);

  // ── source selection ───────────────────────────────────────────────────────
  // ONE source for the life of the graph. Synthetic and live are never mixed:
  // a store holding both would make every count, capture and parity assertion
  // meaningless, and a `lab:` id could end up standing next to a real agent.
  const mode: GraphSourceMode = useMemo(() => currentGraphSourceMode(), []);
  const liveSource = useMemo(
    () => (mode === 'live'
      ? new CanonicalGraphSource({
        fetchStructure: fetchOsStructure,
        fetchRevision: fetchOsRevision,
      })
      : null),
    [mode],
  );
  const staticSource = useMemo(
    () => (mode === 'live' ? null : new StaticRexyGraphSource(presetFromQuery())),
    [mode],
  );
  const source = (liveSource || staticSource)!;
  const rootId = mode === 'live' ? OS_ROOT_ID : LAB_CITY_HALL_ID;

  const [parity, setParity] = useState<ParityIssue[]>([]);
  const [liveCounts, setLiveCounts] = useState<CanonicalCounts | null>(null);
  const [revision, setRevision] = useState<string>('—');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [lastNav, setLastNav] = useState<string>('—');

  const [positions, setPositions] = useState<GraphPositions | null>(null);
  const [buffers, setBuffers] = useState<GraphBuffers | null>(null);
  // Renamed from `mode` when the source mode arrived: two different "modes" in
  // one component is exactly how a webgl/fallback readout ends up reporting a
  // data source. This one is the RENDERER's context.
  const [renderMode, setRenderMode] = useState<GraphRenderMode>('fallback');
  const [stats, setStats] = useState<GraphStats | null>(null);
  const [level, setLevel] = useState<SemanticLevel>(0);
  // Live domain slugs are not known until the structure arrives, so live starts
  // with an empty domain set and is filled from the loaded store below. Seeding
  // it with the lab's nine would put chips on screen for domains this graph may
  // not contain.
  const [filters, setFilters] = useState<GraphFilterState>(
    () => defaultFilterState(mode === 'live' ? [] : LAB_DOMAIN_SLUGS),
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<RexyGraphNodeRecord[]>([]);
  const [sparks, setSparks] = useState<SparkBuild>({ chunks: [], count: 0, buildMs: 0 });
  const [timings, setTimings] = useState<{ layoutMs: number; ranIn: string; bufferMs: number } | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<string[]>([]);

  // Dock state, restored from local storage and validated on the way in.
  const [ui, setUi] = useState<GraphUiState>(() => loadUiState());
  const [searchActive, setSearchActive] = useState(false);
  const [labelCount, setLabelCount] = useState(0);
  /** Live hover chain, polled only while the Dev tab is open. */
  const [hoverTrace, setHoverTrace] = useState<any>(null);
  // True when the dock was opened BY a selection rather than by the owner, so
  // clearing the selection can close it again without shutting a panel he
  // deliberately opened.
  const autoOpenedRef = useRef(false);
  // A mirror of ui.open readable OUTSIDE a state updater. React may invoke an
  // updater twice; the second call saw open already true and cleared the
  // auto-open flag, so deselecting left the panel it had opened still sitting
  // there. Decide from this, mutate the ref here, and keep the updater pure.
  const openRef = useRef(false);
  openRef.current = ui.open;
  const coarsePointer =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;

  useEffect(() => { saveUiState(ui); }, [ui]);

  const setDockTab = useCallback((tab: GraphDockTab) => {
    setUi((prev) => {
      // Clicking the active tab's icon collapses the dock — the rail is a toggle,
      // not a permanently open sidebar.
      const closing = prev.open && prev.activeTab === tab;
      autoOpenedRef.current = false;
      return { ...prev, open: !closing, activeTab: tab };
    });
  }, []);

  const nonceRef = useRef(0);
  // What the owner is currently attending to. Edge LOD resolves local detail
  // around THIS, which is why entering a borough brings its wiring up.
  const focusContextRef = useRef<{
    domainId: string | null; agentId: string | null; clusterId: string | null; nodeId: string | null;
  }>({ domainId: null, agentId: null, clusterId: null, nodeId: null });
  /** Locality tallies filled by edgeLod during the buffer build. */
  const lodStatsRef = useRef({ inDomain: 0, inAgent: 0, inCluster: 0, direct: 0 });
  const selectedRef = useRef<string | null>(null);

  // ── layout + buffers ───────────────────────────────────────────────────────
  // Runs on a snapshot, on a patch, and on a level or filter change. Never per
  // frame; never on a slider (those are uniforms).
  const rebuild = useCallback(async () => {
    const now = () => performance.now();
    const run = await runLayout(store, now);
    const started = now();
    lodStatsRef.current = { inDomain: 0, inAgent: 0, inCluster: 0, direct: 0 };
    const visibility = buildVisibility(
      store, filters, level, focusContextRef.current, selectedRef.current, lodStatsRef.current,
      mode === 'live',
    );
    const built = buildGraphBuffers(store, run.positions, visibility);
    // Sparks are sampled from the SAME edge chunks the line layer draws, so every
    // filter and level decision is inherited rather than re-derived.
    // LAZY: nothing is sampled, allocated or uploaded while sparks are off.
    const sparkBuild = ui.edgeSparksEnabled
      ? buildSparks(built.edges, level, now)
      : { chunks: [], count: 0, buildMs: 0 };
    setPositions(run.positions);
    setBuffers(built);
    setSparks(sparkBuild);
    setTimings({ layoutMs: Math.round(run.ms), ranIn: run.ranIn, bufferMs: Math.round(now() - started) });
  }, [store, filters, level, ui.edgeSparksEnabled]);

  useEffect(() => {
    let cancelled = false;
    source.loadRoot().then((snapshot) => {
      if (cancelled) return;
      store.applySnapshot(snapshot);
      setBreadcrumbs([rootId]);
      setRevision(String(snapshot.revision));
      if (liveSource) {
        setParity(liveSource.getParity());
        setLiveCounts(liveSource.getCounts());
        // Domain chips come from what actually arrived, so a domain added to the
        // OS tomorrow gains a chip with no edit here.
        const slugs = domainSlugsIn(store);
        setFilters((f) => {
          const domains: { [k: string]: boolean } = {};
          slugs.forEach((s) => { domains[s] = true; });
          return { ...f, domains };
        });
      }
      rebuild();
    }).catch((err: Error) => {
      if (cancelled) return;
      // A failed live load must READ as a failure. Falling back to the lab would
      // put synthetic records on a surface the reviewer asked for live truth on,
      // which is the one thing this mode exists to prevent.
      setLoadError(
        err instanceof OsStructureAuthError
          ? 'LIVE STRUCTURE UNAVAILABLE — authenticated Rexy session required'
          : 'LIVE STRUCTURE UNAVAILABLE — ' + err.message,
      );
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, store, rootId]);

  // Filter and level changes rebuild the visible set out of the same store —
  // semantic zoom is visibility, never deletion.
  useEffect(() => {
    if (store.nodeCount) rebuild();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, level]);

  // THE ZERO-SYNTHETIC ASSERTION, counted from the STORE on every rebuild —
  // never from the DTO and never from the adapter's intent, so a synthetic
  // record arriving by any route would show up here. Recomputed on `buffers` so
  // it stays honest across a REFRESH STRUCTURE patch, not just the first load.
  const labNodeCount = useMemo(() => {
    let count = 0;
    store.forEachNode((n) => { if (isLabId(n.id)) count++; });
    return count;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, buffers]);

  // ── navigation ─────────────────────────────────────────────────────────────
  const focus = useCallback((id: string) => {
    const node = store.getNode(id);
    if (node) {
      const isCluster = node.kind === 'memory-cluster';
      focusContextRef.current = {
        domainId: node.domainId || (node.kind === 'domain' ? node.id : null),
        agentId: node.agentId || (node.kind === 'agent' ? node.id : null),
        clusterId: isCluster ? node.id : (node.parentId && store.getNode(node.parentId) &&
          store.getNode(node.parentId)!.kind === 'memory-cluster' ? node.parentId : null),
        nodeId: node.id,
      };
    }
    nonceRef.current += 1;
    setFocusRequest({ id, nonce: nonceRef.current });
    setBreadcrumbs((crumbs) => {
      const at = crumbs.indexOf(id);
      if (at >= 0) return crumbs.slice(0, at + 1);
      const node = store.getNode(id);
      if (!node) return crumbs;
      // Rebuild the trail from the node's own ancestry rather than from the
      // order things were clicked: a search result three levels down must show
      // where it lives, not how it was reached.
      const trail: string[] = [];
      let cursor: RexyGraphNodeRecord | undefined = node;
      let guard = 0;
      while (cursor && guard++ < 12) {
        trail.unshift(cursor.id);
        cursor = cursor.parentId ? store.getNode(cursor.parentId) : undefined;
      }
      return trail;
    });
  }, [store]);

  const back = useCallback(() => {
    setBreadcrumbs((crumbs) => {
      if (crumbs.length < 2) return crumbs;
      const parent = crumbs[crumbs.length - 2];
      nonceRef.current += 1;
      setFocusRequest({ id: parent, nonce: nonceRef.current });
      setSelectedId(parent);
      return crumbs.slice(0, -1);
    });
  }, []);

  // The HUD polls only when Dev is actually open — no cost otherwise, and never
  // an overlay on the graph.
  useEffect(() => {
    if (!(ui.open && ui.activeTab === 'dev')) return;
    const id = window.setInterval(() => {
      const api = (window as any).__rexyGraphLab;
      if (api && api.hoverTrace) setHoverTrace(api.hoverTrace());
    }, 120);
    return () => window.clearInterval(id);
  }, [ui.open, ui.activeTab]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') back(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [back]);

  // SELECTION OPENS THE INSPECTOR; DESELECTION CLOSES WHAT SELECTION OPENED.
  const selectNode = useCallback((id: string | null) => {
    selectedRef.current = id;
    setSelectedId(id);
    if (id) {
      if (!openRef.current) autoOpenedRef.current = true;
      setUi((prev) => ({ ...prev, open: true, activeTab: 'inspector' }));
      return;
    }
    // Deselection closes only what selection opened. A panel the owner opened
    // himself stays open — clearing a selection is not a request to tidy up.
    if (autoOpenedRef.current) {
      autoOpenedRef.current = false;
      setUi((prev) => ({ ...prev, open: false }));
    }
  }, []);

  const onSearch = useCallback((q: string) => {
    setQuery(q);
    source.search(q, 12).then(setResults);
  }, [source]);

  // ── the patch door ─────────────────────────────────────────────────────────
  // One entry point for every mutation, used by the dev buttons AND by the
  // capture harness. Proving the acceptance items through the same call the UI
  // uses is what makes them proofs rather than demonstrations.
  const applyPatch = useCallback((patch: RexyGraphPatch) => {
    store.applyPatch(patch);
    rebuild();
    return { nodes: store.nodeCount, edges: store.edgeCount, revision: store.revision };
  }, [store, rebuild]);

  // ── live structural status ─────────────────────────────────────────────────
  // Drives the stale banner. `stale` means "the graph you are looking at is real
  // but no longer known to be current" — a different statement from "unavailable",
  // and the reason a failed refresh never blanks the scene.
  const [liveStale, setLiveStale] = useState<{ stale: boolean; lastGoodAt: number | null;
    error: string | null; }>({ stale: false, lastGoodAt: null, error: null });

  // ── refresh structure ──────────────────────────────────────────────────────
  // Re-reads the OS structure and lands the DIFFERENCE through the same patch
  // door every other mutation uses. It does not restart the graph, drop the
  // renderer or move the camera — a refresh that reloaded would cost the owner
  // his position every time he checked whether anything had changed.
  const refreshStructure = useCallback(async () => {
    if (!liveSource) return { changed: false, revision };
    try {
      const result = await liveSource.refresh();
      if (result.changed && result.patch) applyPatch(result.patch);
      setParity(liveSource.getParity());
      setLiveCounts(liveSource.getCounts());
      setRevision(result.revision);
      setLoadError(null);
      return result;
    } catch (err) {
      // LAST-KNOWN-GOOD SURVIVES A FAILED REFRESH. The store is untouched, the
      // revision is NOT advanced, and the scene keeps rendering what it has —
      // blanking a correct galaxy because one poll failed would be a worse lie
      // than showing it slightly late. The source has already marked itself
      // stale; this only surfaces it.
      setLiveStale(liveSource.getStatus());
      return { changed: false, revision };
    }
  }, [liveSource, applyPatch, revision]);

  // ── LIVE REVISION POLLING ──────────────────────────────────────────────────
  // The graph asks "did anything move?" every 15 seconds — matching the backend's
  // own read-through cache TTL, because polling faster only costs requests
  // without seeing anything sooner. An unchanged revision costs one small JSON
  // body and does NOTHING else: no topology GET, no diff, no patch, no rebuild.
  //
  // LIVE ONLY, AND EXACTLY ONE TIMER. The effect keys on `liveSource`, which is
  // itself memoised on the source mode, so switching to synthetic tears the timer
  // down and never starts another. Synthetic has no revision to poll.
  useEffect(() => {
    if (!liveSource) return undefined;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      const outcome = await liveSource.poll();
      if (cancelled) return;
      if (outcome === 'patched') {
        // The diff arrives through subscribe() below; here we only refresh the
        // readouts that describe the new snapshot.
        setParity(liveSource.getParity());
        setLiveCounts(liveSource.getCounts());
        setRevision(liveSource.getStatus().revision);
      }
      setLiveStale(liveSource.getStatus());
    };

    // Patches land through the same door every other mutation uses, so the
    // camera, the selection and the layout are preserved for free.
    const unsubscribe = liveSource.subscribe((patch) => {
      if (!cancelled) applyPatch(patch);
    });

    const timer = setInterval(() => { void tick(); }, DEFAULT_POLL_MS);
    return () => {
      cancelled = true;
      unsubscribe();
      clearInterval(timer);
    };
  }, [liveSource, applyPatch]);

  // ── open ───────────────────────────────────────────────────────────────────
  // Parses the node's own address and hands the intent to the host. Nothing here
  // knows what a domain screen is; it knows only that the app owns navigation.
  const openNode = useCallback((node: RexyGraphNodeRecord) => {
    const route = node.openTarget && node.openTarget.route;
    const intent = route ? parseRoute(route) : null;
    if (!intent) { setLastNav('unresolved: ' + (route || 'no route')); return; }
    setLastNav(route as string);
    if (onNavigate) onNavigate(intent);
  }, [onNavigate]);

  const effectiveLevel: SemanticLevel =
    filters.levelOverride === null ? level : (filters.levelOverride as SemanticLevel);

  const labExtras = useMemo(() => ({
    applyPatch,
    semanticLevelOverride: (lv: number | null) => setFilters((f) => ({ ...f, levelOverride: lv })),
    focusNode: (id: string) => { setSelectedId(id); focus(id); },
    selectNode: (id: string | null) => selectNode(id),
    search: (q: string) => source.search(q, 12),
    getSelection: () => selectedId,
    getBreadcrumbs: () => breadcrumbs,
    getTimings: () => timings,
    // Stage-A doors: the harness drives the dock exactly as a pointer would.
    openDock: (tab: GraphDockTab) => setUi((p) => ({ ...p, open: true, activeTab: tab })),
    closeDock: () => setUi((p) => ({ ...p, open: false })),
    // Restores the collapsed default so a test starts from a known dock state
    // rather than from whatever a previous session persisted.
    resetDock: () => {
      autoOpenedRef.current = false;
      setUi((p) => ({ ...p, open: false, activeTab: 'search' }));
    },
    getDock: () => ({ open: ui.open, activeTab: ui.activeTab, labelMode: ui.labelMode }),
    getLabelCount: () => labelCount,
    setSparks: (on: boolean) => setUi((p) => ({ ...p, edgeSparksEnabled: on })),
    sparkState: () => ({ enabled: ui.edgeSparksEnabled, chunks: sparks.chunks.length, count: sparks.count }),
    // The level the VISIBLE BUFFERS were built at — which is what a capture is
    // actually showing. The renderer's cameraLevel is what the camera derives;
    // when an override is held the two differ, and reporting the wrong one would
    // mislabel every semantic-zoom capture.
    appliedLevel: () => effectiveLevel,
    storeCounts: () => ({ nodes: store.nodeCount, edges: store.edgeCount }),
    // ── G2.0 doors ───────────────────────────────────────────────────────────
    // The parity harness drives the SAME calls the dock does, so what it asserts
    // is what the owner sees rather than a parallel implementation of it.
    sourceMode: () => mode,
    structureRevision: () => revision,
    structureCounts: () => liveCounts,
    parityIssues: () => parity,
    structureError: () => loadError,
    labNodeCount: () => labNodeCount,
    refreshStructure: () => refreshStructure(),
    openNode: (id: string) => {
      const node = store.getNode(id);
      if (node) openNode(node);
      return lastNav;
    },
    lastNav: () => lastNav,
    edgeLodState: () => ({
      storeEdges: store.edgeCount,
      renderEdges: buffers ? buffers.edgeCount : 0,
      byPriority: buffers ? buffers.edgesByPriority : [0, 0, 0, 0],
      level: effectiveLevel,
      focus: focusContextRef.current,
      local: lodStatsRef.current,
    }),
  }), [applyPatch, focus, source, selectedId, breadcrumbs, timings, store, effectiveLevel, ui,
    labelCount, buffers, mode, revision, liveCounts, parity, loadError, refreshStructure,
    openNode, lastNav, selectNode, labNodeCount]);

  useEffect(() => {
    if (!IS_DEV) return;
    (window as any).__rexyGraphStats = stats;
  }, [stats]);

  // Teardown the renderer cannot do for us. The renderer disposes its own RAF,
  // controls, observer, geometries, materials, render target and context; the
  // layout worker is a module singleton owned up here, so it is released here.
  useEffect(() => () => {
    disposeLayoutWorker();
    delete (window as any).__rexyGraphStats;
  }, []);

  const selected = selectedId ? store.getNode(selectedId) || null : null;
  const resultIds = useMemo(() => results.map((r) => r.id), [results]);

  useEffect(() => {
    if (filters.levelOverride !== null) setLevel(filters.levelOverride as SemanticLevel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.levelOverride]);

  return (
    <div style={embedded ? embeddedStyle : pageStyle}>
      <RexyGraphViewport
        overlay={
          <GraphDock
            open={ui.open}
            activeTab={ui.activeTab}
            hasSelection={!!selectedId}
            coarsePointer={coarsePointer}
            onToggle={setDockTab}
          >
            {ui.activeTab === 'structure' && (
              <RexyGraphStructureTree
                store={store}
                rootId={rootId}
                live={mode === 'live'}
                selectedId={selectedId}
                onSelect={selectNode}
                onFocusNode={focus}
                onOpen={openNode}
              />
            )}

            {ui.activeTab === 'search' && (
              <GraphSearchPanel
                store={store}
                live={mode === 'live'}
                query={query}
                results={results}
                breadcrumbs={breadcrumbs}
                onQuery={onSearch}
                onFocusChange={setSearchActive}
                onSelect={selectNode}
                onFocusNode={focus}
                onBack={back}
              />
            )}

            {ui.activeTab === 'filters' && (
              <RexyGraphFilters
                state={filters}
                domainSlugs={domainSlugsIn(store)}
                onChange={setFilters}
                devMode={false}
                // LIVE offers only the four groups it has nodes for; memory,
                // files and systems arrive with G2.1's deep structure. Synthetic
                // keeps the full stress filter set.
                kindGroups={mode === 'live' ? LIVE_KIND_GROUPS : undefined}
                showTrust={mode !== 'live'}
              />
            )}

            {ui.activeTab === 'display' && (
              <div>
                <div style={dockPanelTitleStyle}>display</div>
                <LabelModeRow
                  mode={ui.labelMode}
                  onChange={(labelMode) => setUi((p) => ({ ...p, labelMode }))}
                />
                <Slider label="node scale" value={filters.nodeScale} min={0.5} max={2.5} step={0.1}
                  onChange={(v) => setFilters((f) => ({ ...f, nodeScale: v }))} />
                <Slider label="link opacity" value={filters.edgeAlphaScale} min={0} max={4} step={0.1}
                  onChange={(v) => setFilters((f) => ({ ...f, edgeAlphaScale: v }))} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ color: '#7286a0', width: 74 }}>sparks</span>
                  <button type="button" onClick={() => setUi((p) => ({ ...p, edgeSparksEnabled: !p.edgeSparksEnabled }))}
                    style={{ padding: '3px 7px', background: 'transparent', borderRadius: 2, cursor: 'pointer',
                      border: '1px solid ' + (ui.edgeSparksEnabled ? 'rgba(120,190,255,0.45)' : 'rgba(120,160,200,0.18)'),
                      color: ui.edgeSparksEnabled ? '#cfe6ff' : '#7286a0', fontFamily: 'inherit', fontSize: 9.5 }}>
                    {ui.edgeSparksEnabled ? 'on' : 'off'}
                  </button>
                </div>
                {ui.edgeSparksEnabled && (
                  <Slider label="spark opacity" value={ui.sparkOpacity} min={0} max={3} step={0.1}
                    onChange={(v) => setUi((p) => ({ ...p, sparkOpacity: v }))} />
                )}
                <div style={{ display: 'flex', gap: 4, marginTop: 12 }}>
                  <DockAction label="fit graph" onClick={() => {}} disabled />
                  <DockAction label="return to rexy" onClick={() => {}} disabled />
                </div>
                <div style={{ marginTop: 8, color: '#4e6076', lineHeight: 1.5 }}>
                  Fit and Return arrive with the camera stage.
                </div>
                {mode === 'live' && (
                  <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid rgba(120,180,255,0.12)' }}>
                    <DockAction label="refresh structure" onClick={() => { void refreshStructure(); }} />
                    <div style={{ marginTop: 6, color: '#4e6076', lineHeight: 1.5 }}>
                      Re-reads domains, agents and surfaces and applies the difference.
                      The camera, the selection and the renderer are untouched.
                    </div>
                  </div>
                )}
              </div>
            )}

            {ui.activeTab === 'inspector' && (
              <GraphInspectorPanel
                store={store}
                node={selected}
                onSelect={selectNode}
                onFocusNode={focus}
                onOpen={openNode}
              />
            )}

            {ui.activeTab === 'dev' && (
              <div>
                <div style={dockPanelTitleStyle}>dev</div>
                {/* SOURCE FIRST, AND NEVER LEFT TO INFERENCE. A reviewer must not
                    have to work out from node counts whether he is looking at
                    Rexy or at the stress harness. */}
                <Row k="SOURCE" v={sourceModeLabel(mode)} />
                {mode === 'live' ? (
                  <>
                    <Row k="revision" v={revision} />
                    <Row k="domains" v={liveCounts ? String(liveCounts.domains) : '—'} />
                    {/* TYPED, NEVER COLLAPSED. "agents 38" cannot be read off
                        this panel: a primary agent, a team member and a declared-
                        but-unbuilt name are three different facts, and merging
                        them is how the old map claimed 28. */}
                    <Row k="live primary agents" v={liveCounts ? String(liveCounts.livePrimaryAgents) : '—'} />
                    <Row k="teams" v={liveCounts ? String(liveCounts.teams) : '—'} />
                    <Row k="team members" v={liveCounts ? String(liveCounts.teamMembers) : '—'} />
                    <Row k="TOTAL live agent entities" v={liveCounts ? String(liveCounts.totalLiveAgentEntities) : '—'} />
                    <Row k="declared-not-built agents" v={liveCounts ? String(liveCounts.declaredNotBuiltPrimaryAgents) : '—'} />
                    <Row k="orphans" v={liveCounts ? String(liveCounts.orphans) : '—'} />
                    {/* Memory by OWNERSHIP CLASS. A single total would hide the
                        two-layer distinction the whole architecture turns on. */}
                    <Row k="os memory" v={liveCounts ? String(liveCounts.osMemory) : '—'} />
                    <Row k="domain memory" v={liveCounts ? String(liveCounts.domainMemory) : '—'} />
                    <Row k="agent memory" v={liveCounts ? String(liveCounts.agentMemory) : '—'} />
                    <Row k="team memory" v={liveCounts ? String(liveCounts.teamMemory) : '—'} />
                    <Row k="memory files" v={liveCounts ? String(liveCounts.memoryFiles) : '—'} />
                    <Row k="memory records" v={liveCounts ? String(liveCounts.memoryRecords) : '—'} />
                    <Row k="models" v={liveCounts ? String(liveCounts.models) : '—'} />
                    <Row k="databases" v={liveCounts ? String(liveCounts.databases) : '—'} />
                    <Row k="workflows" v={liveCounts ? String(liveCounts.workflows) : '—'} />
                    <Row k="services" v={liveCounts ? String(liveCounts.services) : '—'} />
                    <Row k="surfaces" v={liveCounts ? String(liveCounts.surfaces) : '—'} />
                    <Row k="TOTAL NODES" v={liveCounts ? String(liveCounts.totalNodes) : '—'} />
                    <Row k="TOTAL RELATIONS" v={liveCounts ? String(liveCounts.totalRelations) : '—'} />
                    <Row k="parity issues" v={String(parity.length)} />
                    {/* The boundary proof: structure was mapped, content was never
                        entered. Live must report zero lab nodes below. */}
                    {/* THE ZERO-SYNTHETIC ASSERTION, COUNTED FROM THE STORE ITSELF.
                        Not from the adapter's intent and not from the DTO — from
                        what is actually loaded, so a synthetic record that reached
                        the store by any route would show up here. */}
                    <Row k="lab node count" v={String(labNodeCount)} />
                    <Row k="last open" v={lastNav} />
                    {loadError && <div style={{ ...rowStyle, color: '#FF9D2E' }}>{loadError}</div>}
                    {/* STALE, NOT UNAVAILABLE. The graph on screen is real and
                        still rendered; only its freshness is in doubt. Blanking
                        a correct galaxy because one poll failed would be the
                        bigger lie, so this is a status line, never a takeover. */}
                    {liveStale.stale && (
                      <div style={{ ...rowStyle, color: '#FFD84A' }}>
                        LIVE UPDATE UNAVAILABLE — last updated{' '}
                        {liveStale.lastGoodAt
                          ? new Date(liveStale.lastGoodAt).toLocaleTimeString()
                          : 'unknown'}
                      </div>
                    )}
                    {parity.length > 0 && (
                      <div style={{ maxHeight: 150, overflowY: 'auto', marginTop: 4,
                        paddingTop: 4, borderTop: '1px solid rgba(120,180,255,0.12)' }}>
                        {parity.map((p, i) => (
                          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 3 }}>
                            <span style={{ color: p.severity === 'drift' ? '#FF9D2E'
                              : p.severity === 'ambiguity' ? '#FFD84A' : '#4e6076', width: 58, flex: 'none' }}>
                              {p.severity}
                            </span>
                            <span style={{ color: '#9fb4cc' }}>{p.entity}: {p.issue}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ marginBottom: 6 }} />
                  </>
                ) : null}
                <Row k="context" v={renderMode} />
                <Row k="level" v={'L' + effectiveLevel + (filters.levelOverride !== null ? ' (held)' : '')} />
                <Row k="visible nodes" v={buffers ? String(buffers.nodeCount) : '—'} />
                <Row k="render edges" v={buffers ? String(buffers.edgeCount) : '—'} />
                <Row k="  P4/P3/P2/P1" v={buffers ? buffers.edgesByPriority.slice().reverse().join(' / ') : '—'} />
                <Row k="store nodes" v={String(store.nodeCount)} />
                <Row k="store edges" v={String(store.edgeCount)} />
                <div style={{ margin: '8px 0 6px', paddingTop: 6,
                  borderTop: '1px solid rgba(120,180,255,0.12)', color: '#4e6076',
                  letterSpacing: 1.4, fontSize: 9 }}>HOVER CHAIN</div>
                <Row k="POINTER" v={hoverTrace && hoverTrace.pointer
                  ? hoverTrace.pointer.join(', ') + (hoverTrace.suppressed ? ' (suppressed)' : '')
                  : 'outside'} />
                <Row k="CANDIDATE" v={hoverTrace && hoverTrace.candidate
                  ? String(hoverTrace.candidate).split(':').slice(-2).join(':') + ' ' + hoverTrace.candidateAgeMs + 'ms'
                  : '—'} />
                <Row k="VISIBLE" v={hoverTrace && hoverTrace.visible
                  ? String(hoverTrace.visible).split(':').slice(-2).join(':') : '—'} />
                <Row k="FEATHER" v={hoverTrace && hoverTrace.visible ? 'mounted' : 'none'} />
                <div style={{ marginBottom: 6 }} />
                <Row k="edge sparks" v={String(sparks.count)} />
                <Row k="spark build" v={sparks.buildMs.toFixed(0) + 'ms'} />
                <Row k="labels" v={String(labelCount)} />
                <Row k="fps" v={stats ? String(stats.fps) : '—'} />
                <Row k="draw calls" v={stats ? String(stats.drawCalls) : '—'} />
                <Row k="programs" v={stats ? String(stats.programs) : '—'} />
                <Row k="geometries" v={stats ? String(stats.geometries) : '—'} />
                <Row k="layout" v={timings ? timings.layoutMs + 'ms ' + timings.ranIn : '—'} />
                <Row k="buffers" v={timings ? timings.bufferMs + 'ms' : '—'} />
                <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(120,180,255,0.12)',
                  color: '#4e6076', fontSize: 9.5 }}>
                  {mode === 'live'
                    ? 'live os: structure — domains, agents and surfaces only'
                    : 'synthetic lab: source — not Rexy topology'}
                </div>
              </div>
            )}
          </GraphDock>
        }
      >
        {buffers && positions ? (
          <RexySpatialGraphThree
            buffers={buffers}
            positions={positions}
            store={store}
            sparks={sparks.chunks}
            sparkOpacity={ui.sparkOpacity}
            selectedId={selectedId}
            hoveredId={hoveredId}
            focusRequest={focusRequest}
            labelMode={ui.labelMode}
            searchResultIds={resultIds}
            searchActive={searchActive}
            edgeAlphaScale={filters.edgeAlphaScale}
            nodeScale={filters.nodeScale}
            onSelect={selectNode}
            onHover={setHoveredId}
            onLevel={(lv) => { if (filters.levelOverride === null) setLevel(lv); }}
            onMode={setRenderMode}
            onStats={setStats}
            onLabelCount={setLabelCount}
            labExtras={labExtras}
          />
        ) : null}
      </RexyGraphViewport>
    </div>
  );
}

/** Domain slugs actually present — so a domain ADDED BY PATCH gains a filter chip. */
function domainSlugsIn(store: RexyGraphStore): string[] {
  const ids = store.nodesOfKind('domain');
  const slugs: string[] = [];
  for (let i = 0; i < ids.length; i++) slugs.push(domainSlug(ids[i]));
  slugs.sort();
  return slugs;
}

function buildVisibility(
  store: RexyGraphStore,
  filters: GraphFilterState,
  level: SemanticLevel,
  focus: { domainId: string | null; agentId: string | null; clusterId: string | null; nodeId: string | null },
  selectedId: string | null,
  stats: { inDomain: number; inAgent: number; inCluster: number; direct: number },
  live: boolean,
): GraphVisibility {
  // Deterministic sampling: the SAME distant wiring appears after every reload,
  // so the geography stays learnable instead of shimmering between sessions.
  const sample = (edgeId: string, fraction: number) => hashUnit(edgeId, 'lod') < fraction;

  const directOfSelected: { [id: string]: true } = {};
  if (selectedId) {
    const own = store.edgesOf(selectedId);
    for (let i = 0; i < own.length; i++) directOfSelected[own[i]] = true;
  }

  return {
    alphaScale(node) {
      return levelAlphaFor(node.kind, level);
    },
    edgeAlphaScale(_edge, sourceKind, targetKind) {
      const a = levelAlphaFor(sourceKind as never, level);
      const b = levelAlphaFor(targetKind as never, level);
      return Math.min(a, b);
    },

    /**
     * SEMANTIC EDGE LOD — what is rasterised, never what exists.
     *
     * THE LADDER IS LOCAL, NOT GLOBAL. G1.3 rendered the same 13,346 edges at L1
     * and L2, which defeated the whole point: descending must reveal the SUBTREE
     * the owner is standing in, while the rest of Rexy stays structurally quiet.
     * So distant content holds the L0 baseline at every level and the extra
     * detail is granted by locality — domain, then agent, then cluster, then the
     * focused node's own relations.
     *
     * Total counts are therefore NOT monotonic, and should not be: L2 can render
     * fewer edges than L1 while showing far more of the agent you entered.
     */
    edgeLod(edge, priority) {
      if (directOfSelected[edge.id]) return true;
      if (priority >= 4) return true;

      const source = store.getNode(edge.sourceId);
      const target = store.getNode(edge.targetId);
      if (!source || !target) return false;

      const inDomain = !!focus.domainId &&
        source.domainId === focus.domainId && target.domainId === focus.domainId;
      const inAgent = !!focus.agentId &&
        source.agentId === focus.agentId && target.agentId === focus.agentId;
      const inCluster = !!focus.clusterId &&
        (source.parentId === focus.clusterId || source.id === focus.clusterId) &&
        (target.parentId === focus.clusterId || target.id === focus.clusterId);
      const touchesFocusNode = !!focus.nodeId &&
        (edge.sourceId === focus.nodeId || edge.targetId === focus.nodeId);

      // The distant baseline, identical at every level. This is what keeps the
      // L0 gain from eroding as the owner descends.
      const distant = () => {
        if (priority === 3) return true;
        if (priority === 2) return sample(edge.id, 0.15);
        return sample(edge.id, 0.03);
      };

      let show: boolean;
      if (level === 0) {
        show = distant();
      } else if (level === 1) {
        // The focused BOROUGH resolves.
        show = inDomain ? (priority >= 2 ? true : sample(edge.id, 0.25)) : distant();
      } else if (level === 2) {
        // The focused MANSION resolves completely; its borough keeps L1 richness.
        if (inAgent) show = true;
        else if (inDomain) show = priority >= 2 ? true : sample(edge.id, 0.25);
        else show = distant();
      } else if (level === 3) {
        // The focused MEMORY CLUSTER resolves completely.
        if (inCluster || touchesFocusNode) show = true;
        else if (inAgent) show = priority >= 2 ? true : sample(edge.id, 0.6);
        else if (inDomain) show = priority >= 2;
        else show = distant();
      } else {
        // L4 — the focused RECORD's own relations, always, plus its cluster.
        if (touchesFocusNode || inCluster) show = true;
        else if (inAgent) show = priority >= 2;
        else if (inDomain) show = priority >= 3;
        else show = distant();
      }

      if (show) {
        if (inDomain) stats.inDomain++;
        if (inAgent) stats.inAgent++;
        if (inCluster) stats.inCluster++;
        if (touchesFocusNode) stats.direct++;
      }
      return show;
    },

    node(node) {
      if (node.orphan && !filters.orphans) return false;
      const group = live ? liveGroupForNode(node) : (KIND_GROUP[node.kind] || 'files');
      if (!filters.kinds[group]) return false;
      if (node.trustTier && !filters.trust[node.trustTier]) return false;
      if (node.domainId) {
        const slug = domainSlug(node.domainId);
        if (Object.prototype.hasOwnProperty.call(filters.domains, slug) && !filters.domains[slug]) {
          return false;
        }
      }
      return true;
    },
    edge(edge) {
      if (edge.crossDomain && !filters.crossDomainEdges) return false;
      if (Object.prototype.hasOwnProperty.call(filters.relations, edge.kind) && !filters.relations[edge.kind]) {
        return false;
      }
      return true;
    },
  };
}

function LabelModeRow({ mode, onChange }: {
  mode: 'selection' | 'context' | 'all';
  onChange: (m: 'selection' | 'context' | 'all') => void;
}): React.ReactElement {
  const modes: Array<'selection' | 'context' | 'all'> = ['selection', 'context', 'all'];
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ color: '#4e6076', marginBottom: 4 }}>labels</div>
      <div style={{ display: 'flex', gap: 4 }}>
        {modes.map((m) => (
          <button key={m} type="button" onClick={() => onChange(m)} style={{
            padding: '3px 7px', background: 'transparent', borderRadius: 2, cursor: 'pointer',
            border: '1px solid ' + (mode === m ? 'rgba(120,190,255,0.45)' : 'rgba(120,160,200,0.18)'),
            color: mode === m ? '#cfe6ff' : '#7286a0',
            fontFamily: 'inherit', fontSize: 9.5, letterSpacing: 0.6,
          }}>{m}</button>
        ))}
      </div>
    </div>
  );
}

function Slider({ label, value, min, max, step, onChange }: {
  label: string; value: number; min: number; max: number; step: number;
  onChange: (v: number) => void;
}): React.ReactElement {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
      <span style={{ color: '#7286a0', width: 74 }}>{label}</span>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ flex: 1, accentColor: '#2EA8FF' }} />
      <span style={{ color: '#cfe6ff', width: 26, textAlign: 'right' }}>{value.toFixed(1)}</span>
    </label>
  );
}

function DockAction({ label, onClick, disabled }: {
  label: string; onClick: () => void; disabled?: boolean;
}): React.ReactElement {
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{
      padding: '3px 7px', background: 'transparent',
      border: '1px solid rgba(120,190,255,0.22)', borderRadius: 2,
      color: disabled ? '#3d4c5e' : '#cfe6ff',
      cursor: disabled ? 'default' : 'pointer',
      fontFamily: 'inherit', fontSize: 9.5, letterSpacing: 0.6,
    }}>{label}</button>
  );
}

function Row({ k, v }: { k: string; v: string }): React.ReactElement {
  return (
    <div style={rowStyle}>
      <span style={rowKeyStyle}>{k}</span>
      <span style={rowValStyle}>{v}</span>
    </div>
  );
}

const pageStyle: React.CSSProperties = { position: 'fixed', inset: 0, background: GRAPH_BACKGROUND_HEX };

// Embedded: a flex child that takes the room left below the header and tab row,
// and no more. `position: fixed` here would paint over the header, the tab
// switch and the bottom DomainBar — the graph would be the only thing on screen
// and there would be no way back out of the tab.
const embeddedStyle: React.CSSProperties = {
  position: 'relative',
  flex: 1,
  minHeight: 0,
  background: GRAPH_BACKGROUND_HEX,
};

const leftColumnStyle: React.CSSProperties = {
  position: 'absolute', top: 14, left: 14, display: 'flex', flexDirection: 'column', gap: 10,
};

const statsStyle: React.CSSProperties = {
  minWidth: 264, padding: '10px 12px',
  background: 'rgba(4,10,22,0.72)', border: '1px solid rgba(120,180,255,0.16)',
  borderRadius: 4, backdropFilter: 'blur(8px)',
  color: '#9fb4cc', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 10.5, letterSpacing: 0.6, lineHeight: 1.7, pointerEvents: 'none',
};

const statsTitleStyle: React.CSSProperties = {
  color: '#cfe6ff', letterSpacing: 1.4, marginBottom: 6, fontSize: 9.5,
};

const statsFootStyle: React.CSSProperties = {
  marginTop: 8, paddingTop: 6, borderTop: '1px solid rgba(120,180,255,0.12)',
  color: '#4e6076', fontSize: 9.5,
};

const rowStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 18 };
const rowKeyStyle: React.CSSProperties = { color: '#6d8098' };
const rowValStyle: React.CSSProperties = { color: '#cfe6ff' };
